import React from 'react';
import css from './index.module.scss';
import { ScaleLinear, ScaleBand } from 'd3';
import { classNames, numberWithCommas } from '@fullstory/util/fmt';
import { TickValues, generateTickValues } from './utils';
import ReactDOM from 'react-dom';
import { OverflowText } from '@fullstory/components';

interface ScaleYAxisProps {
  /** the current d3 scale to generate the axis from */

  scale: ScaleLinear<number, number>;
  /** the width of the tick line, pointing to the number */
  tickWidth: number;
  /** additional padding in between the tick line and the label text */
  tickPadding: number;
  /** additional Y-axis padding to add to the text, allowing each number to be centered around the current tick. */
  textYPadding: number;
  /** Optional - if given, the tick values will exactly match the domain (current value) and range (placement) for each tick */
  tickValues?: TickValues[];
  tickCount?: number;
  /**
   * controls the expected orientation of the scale, if set to left, the scale is expected to
   * be on the left hand of the graph, and on the right for the right version.
   * left: |- num
   * right:   num -|
   */
  alignment: 'right' | 'left';
  /** If given, this will apply an additional transform to the surrounding svg `g` element. */
  transform: string;
  /** If given, this will provide the ability to customize the text output */
  textRenderer?: (d: TickValues) => React.ReactNode;
  /** Optional - if true, the tick lines are hidden */
  hideLines?: boolean;
}

/**
 * A helper class to generate a Y axis from a given d3 scale.
 * WARNING: If the height of the scale exactly matches the height of the current SVG, the text will be clipped.
 * It's expected that the parent SVG has margins built in.
 *
 * Y Axis scales are also expected to be in reverse order:
 * ```
 * let margin = 10;
 * d3.scale.linear().domain([0,1]).range([300 - margin, margin])
 * ```
 */
export class ScaleYAxis extends React.Component<ScaleYAxisProps> {
  static defaultProps: ScaleYAxisProps = {
    scale: {} as ScaleLinear<number, number>,
    tickWidth: 6,
    tickPadding: 4,
    alignment: 'left',
    textYPadding: 5,
    transform: '',
    hideLines: false,
  };
  render() {
    const { scale, tickValues, tickPadding, tickWidth, alignment, hideLines } = this.props;
    const range = scale.range();
    const values = tickValues ? tickValues : generateTickValues(scale, this.props.tickCount);
    const textOffset = hideLines
      ? 0
      : this.props.alignment === 'left'
      ? tickPadding + tickWidth
      : -tickPadding;

    const lineOffset = alignment === 'left' ? 1 : tickWidth;
    const textClass = classNames(css.axisValue, {
      [css.rightAlign]: alignment === 'right',
    });
    return (
      <g data-component="ScaleYAxis" className={css.scaleAxis} transform={this.props.transform}>
        {!hideLines && (
          <line
            x1={lineOffset}
            x2={lineOffset}
            y1={range[0]}
            y2={range[1]}
            className={css.axisLine}
          />
        )}
        {values.map((d: TickValues, i: number) => (
          <React.Fragment key={i}>
            {!hideLines && (
              <line
                key={`l-${i}`}
                className={css.axisLine}
                x1={0}
                x2={0 + this.props.tickWidth}
                y1={d.position}
                y2={d.position}
              />
            )}
            <text
              key={`t-${i}`}
              className={textClass}
              x={textOffset}
              y={d.position + this.props.textYPadding}
            >
              {this.props.textRenderer ? this.props.textRenderer(d) : numberWithCommas(d.value)}
            </text>
          </React.Fragment>
        ))}
      </g>
    );
  }
}

interface ScaleYGraphLinesProps {
  scale: ScaleLinear<number, number>;
  width: number;
  marginLeft: number;
  marginRight: number;
  tickValues?: TickValues[];
}

export function ScaleYGraphLines(props: ScaleYGraphLinesProps) {
  const { scale, marginLeft, marginRight, width, tickValues } = props;
  const ticks = tickValues || generateTickValues(scale);
  return (
    <>
      {ticks.map((d: TickValues, i: number) => (
        <line
          key={i}
          className={css.graphLine}
          x1={marginLeft}
          x2={width - marginRight}
          y1={d.position}
          y2={d.position}
        />
      ))}
    </>
  );
}

interface BottomOrdinalAxisProps {
  xScale: ScaleBand<string>;
  // offset the container by Y
  transformY: number;
  // offset the containerY
  transformX: number;
  // optional classname, applied to the surrounding `g` element
  className?: string;
  // optional classname, applied to the individual label.
  labelClassName?: string;
  /**
   * y position for each indvidiual text item.
   * Since SVG has a flipped coordinate system ([0,0] is the upper left),
   * this position should at least be equal to height of the text,
   * otherwise the label will be clipped.
   */
  y?: number;
  // optional prop that can be used to override the display labels
  labels?: string[];
  /**
   * Add additional offset to x positioning of each label, in addition to the
   * leftmost point of the range band. If left unset, it will default to the
   * center of the range band.
   */
  x?: number;
  // Height of the SVG element
  height: number;
  /**
   * If given, this will render the x-axis labels as divs through a react Portal.
   * As long as that div is equal in size to the svg element, this will place the labels
   * in the same coordinate space. In this state, the labels are rendered within an overflow text
   * renderer, allowing the full content to be visualized in a tooltip, but shown with ellips if it overflows.
   */
  portalDiv?: HTMLDivElement;
  /**
   * By default, labels will render within the maximum amount of space for each item in the scale (bar size and it's padding)
   * Setting a value for `stepPadding` increases the distance (in pixels) between each label, similar to a div's padding.
   */
  stepPadding?: number;
}

/**
 * Given a x-scale ordinal d3 scale, render the labels as text in the center of the band.
 */
export class BottomOrdinalAxis extends React.Component<BottomOrdinalAxisProps> {
  static defaultProps: BottomOrdinalAxisProps = {
    xScale: {} as ScaleBand<string>,
    transformX: 0,
    transformY: 0,
    height: 0,
    stepPadding: 0,
    className: '',
    // default assumes a font size of ~14px, set to avoid clipping
    y: 14,
  };

  get x(): number {
    return this.props.x ? this.props.x : this.props.xScale.bandwidth() / 2;
  }

  render() {
    const { xScale } = this.props;
    const axisClassName = classNames(css.bottomOrdinalAxis, 'axis', this.props.className);
    const labelClassName = classNames(
      css.bottomOrdinalAxis,
      css.label,
      'label',
      this.props.labelClassName,
      'fs-unmask',
    );

    if (this.props.portalDiv) {
      // For large screens and/or graphs with few bars, there is more space for labels. Make the label at most the
      // width of the bar plus stepPadding pixels on either side
      const bigBars = (this.x + (this.props.stepPadding ?? 0)) * 2;

      // For small screens and/or graphs with a lot of bars, there isn't enough space for each label to be the full bar
      // width plus stepPadding pixels on either side -- the labels will run into each other
      const range = xScale.range();
      const smallBars = (range[1] - range[0]) / xScale.domain().length;

      const barWidth = Math.min(smallBars, bigBars);
      const allContent = xScale.domain().map((label: string, i: number, arr: string[]) => {
        const displayLabel =
          this.props.labels && this.props.labels[i] ? this.props.labels[i] : label;
        return (
          <OverflowText
            key={i}
            className={labelClassName}
            style={{
              top: this.props.height + (this.props.y ?? 0),
              left: xScale(label)! + this.x,
              maxWidth: `${barWidth}px`,
            }}
          >
            {displayLabel}
          </OverflowText>
        );
      });
      return ReactDOM.createPortal(allContent, this.props.portalDiv);
    }
    return (
      <g
        data-component="BottomOrdinalAxis"
        className={axisClassName}
        transform={`translate(${this.props.transformX}, ${this.props.transformY})`}
      >
        {xScale.domain().map((label, i) => (
          <text key={label} className={css.label} x={xScale(label) ?? 0 + this.x} y={this.props.y}>
            {this.props.labels && this.props.labels[i] ? this.props.labels[i] : label}
          </text>
        ))}
      </g>
    );
  }
}
